import React from "react";
import {
  Utensils,
  ChefHat,
  Wine,
  Shirt,
  Search,
  Film,
  Moon,
  Home,
  Heart,
  Sparkles,
  Coffee,
} from "lucide-react";

// --- The Time Row Component ---
const TimeRow = ({
  time,
  title,
  sub,
  icon: Icon,
  isLast,
  highlight = "neutral",
}) => {
  // Style Logic based on 'highlight' prop
  const styles = {
    neutral: {
      time: "text-stone-400",
      dot: "bg-white border-2 border-stone-200 text-stone-300",
      title: "text-stone-700",
      sub: "text-stone-400",
      container: "",
    },
    hero: {
      // THE MOVIE MOMENT
      time: "text-indigo-600 font-bold",
      dot: "bg-indigo-100 border-2 border-indigo-300 text-indigo-600 shadow-sm shadow-indigo-100",
      title: "text-indigo-900 text-lg",
      sub: "text-indigo-600/80",
      container:
        "bg-white/60 p-3 -mx-3 rounded-xl border border-indigo-100/50 shadow-sm",
    },
    warm: {
      // COOKING
      time: "text-stone-500",
      dot: "bg-orange-50 border-2 border-orange-200 text-orange-500",
      title: "text-stone-800",
      sub: "text-stone-500",
    },
    night: {
      // BEDTIME
      time: "text-slate-400",
      dot: "bg-slate-50 border-2 border-slate-200 text-slate-400",
      title: "text-slate-600",
      sub: "text-slate-400",
    },
  };

  const currentStyle = styles[highlight] || styles.neutral;

  return (
    <div className="flex gap-5 relative group items-start">
      {/* 1. Time Anchor (Left) */}
      <div
        className={`w-14 shrink-0 text-right pt-1.5 font-mono text-sm font-bold tracking-tight ${currentStyle.time}`}
      >
        {time}
      </div>

      {/* 2. Visual Thread (Center) */}
      <div className="relative flex flex-col items-center">
        {/* The Icon Dot */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all ${currentStyle.dot}`}
        >
          <Icon className="w-4 h-4" />
        </div>
        {/* The Connecting Line */}
        {!isLast && (
          <div className="absolute top-8 bottom-[-32px] w-0.5 bg-stone-200/60"></div>
        )}
      </div>

      {/* 3. Narrative Content (Right) */}
      <div className={`flex-1 pb-8 ${isLast ? "pb-0" : ""}`}>
        <div className={currentStyle.container}>
          <h4
            className={`font-bold leading-tight flex items-center gap-2 ${currentStyle.title}`}
          >
            {title}
          </h4>
          <p
            className={`text-xs mt-1 leading-relaxed font-medium ${currentStyle.sub}`}
          >
            {sub}
          </p>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---
const ChristmasEveTimeline = () => {
  return (
    <div className="max-w-2xl mx-auto mb-8 animate-in slide-in-from-bottom-4">
      {/* HEADER */}
      <div className="text-center mb-8 pt-4">
        <h2 className="text-2xl font-bold text-stone-800 flex items-center justify-center gap-2">
          🎄 Christmas Eve Flow
        </h2>
        <p className="text-stone-400 text-xs uppercase tracking-widest font-bold mt-2">
          From dinner to bedtime · Together
        </p>
      </div>

      {/* ACT I: COOK & GATHER (Warm Orange/Red) */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4 ml-20">
          <Utensils className="w-3 h-3 text-orange-500" />
          <span className="text-[10px] font-bold text-orange-600 uppercase tracking-widest">
            Act I: Cook & Gather
          </span>
        </div>

        <div className="bg-orange-50/40 rounded-3xl p-6 border border-orange-100">
          <TimeRow
            time="04:00"
            title="Family Dinner Prep"
            sub="Ribs, Bacon-Wrapped Brussels Sprouts, Pizza, Salad. Shared prep. Music on."
            icon={ChefHat}
            highlight="warm"
          />
          <TimeRow
            time="05:30"
            title="Apps & Baked Goods Out"
            sub="Grazing begins. Kitchen resets itself as we go."
            icon={Wine}
            highlight="warm"
            isLast={true}
          />
        </div>
      </div>

      {/* ACT II: TRADITIONS & MAGIC (Gold/Indigo) */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4 ml-20">
          <Sparkles className="w-3 h-3 text-indigo-500" />
          <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">
            Act II: Traditions & Magic
          </span>
        </div>

        <div className="bg-gradient-to-b from-indigo-50/50 to-white rounded-3xl p-6 border border-indigo-100 shadow-sm">
          <TimeRow
            time="06:30"
            title="Christmas Eve Pajamas"
            sub="Cozy mode activated."
            icon={Shirt}
            highlight="neutral"
          />
          <TimeRow
            time="06:45"
            title="Scavenger Hunt"
            sub="Laughter over speed. Capture moments."
            icon={Search}
            highlight="neutral"
          />
          <TimeRow
            time="07:45"
            title="Christmas Eve Movie 🎬"
            sub="Blankets, snacks, togetherness."
            icon={Film}
            highlight="hero"
            isLast={true}
          />
        </div>
      </div>

      {/* ACT III: WIND DOWN (Dark Slate/Moon) */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4 ml-20">
          <Moon className="w-3 h-3 text-slate-400" />
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            Act III: Wind Down
          </span>
        </div>

        <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100">
          <TimeRow
            time="08:45"
            title="Kids to Bed 💤"
            sub="Lights out. Tomorrow comes early."
            icon={Moon}
            highlight="night"
          />
          <TimeRow
            time="09:00"
            title="House Reset (Light)"
            sub="Just enough for morning peace."
            icon={Home}
            highlight="night"
          />
          <TimeRow
            time="10:30"
            title="Parents Upstairs"
            sub="Gratitude, quiet, rest."
            icon={Heart}
            highlight="night"
            isLast={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ChristmasEveTimeline;
